import { useCallback, useState, useEffect, ChangeEvent } from 'react'
import { IEquipment, IFullStop } from 'types'
import { IVariant, IDetentionShipment } from 'apps/detention-review/utils/types'
import { getCalculatedRate, getMinutes } from 'utils/accessorialHelpers'
import { debounce } from 'utils/debounce'
import { IDetention, ITimeDate, IVariantState } from '../types'
import { updateShipmentAccessorials, IUpdateAccessorialsProps } from '../updateShipmentAccessorials'

const getInitialVariantState = (): IVariantState => ({
  message: '',
  label: '',
  state: '',
  title: '',
})

const getInitialDetentionState = (shipperFacilityUuid: string = ''): IDetention => ({
  id: undefined,
  shipperFacilityUuid,
  title: 'Detention',
  name: 'Detention Fee',
  amount: '',
  file: '',
  fileUrl: '',
  fileName: '',
  additionalInfo: {
    startDateTime: '',
    endDateTime: '',
    trailerControlRecord: '',
    submittedBy: '',
    externalNote: '',
  },
  state: '',
  startAt: '',
  endAt: '',
  startDateTime: '',
  endDateTime: '',
  startDateTimeIsValid: false,
  endDateTimeIsValid: false,
})

interface IDetentionResponse {
  detention: IDetention
  setDetention: (values: IDetention) => void
  setDetentionRate: (value: number) => void
  debouncedSetDetention: () => void
  onChangeAmount: (value: number) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onChangeText: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  onChangeDateTime: ({ date, time }: ITimeDate, id: string) => void
  onClick: (value: IVariant) => void
  onConfirm: (value: string) => void
  dialogVariant: IVariant
  calculatedAmount: number
  isFormValid: boolean
  updateShipmentAccessorials: (props: IUpdateAccessorialsProps) => Partial<IDetentionShipment>
}

/**
 * Stores state and logic in a hook that be be used throughout detention components.
 */
const useDetention = (selectedStop?: IFullStop, equipment?: IEquipment): IDetentionResponse => {
  const [detention, setDetention] = useState<IDetention>(
    getInitialDetentionState(selectedStop?.facility?.shipperFacilityUuid)
  )
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0)
  const [detentionRate, setDetentionRate] = useState<number>(50)
  const [dialogVariant, setDialogVariant] = useState<IVariantState>(getInitialVariantState())
  const debouncedSetDetention = debounce(setDetention, 250)

  useEffect(() => {
    const startDateTime = detention.startDateTime || detention.additionalInfo.startDateTime
    const endDateTime = detention.endDateTime || detention.additionalInfo.endDateTime

    // Set detention rates when start and end times are updated
    if (selectedStop && (startDateTime || endDateTime)) {
      const hourlyRate = detentionRate
      const minutes = getMinutes({
        appointment: selectedStop?.appointment,
        startDateTime,
        endDateTime,
      })
      const calculatedRate = getCalculatedRate({ hourlyRate, minutes })
      setCalculatedAmount(calculatedRate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detention.startDateTime,
    detention.endDateTime,
    detention.additionalInfo.startDateTime,
    detention.additionalInfo.endDateTime,
    equipment,
  ])

  const onChangeAmount = useCallback((value: number) => setCalculatedAmount(value), [])

  const onChange = useCallback(
    ({ target: { id, value } }: ChangeEvent<HTMLInputElement>) =>
      debouncedSetDetention((current: IDetention) => {
        // TODO: please make those TS files, it'll help simplify refactoring
        const newState = {
          ...current,
          ...{ additionalInfo: { ...current.additionalInfo, [id]: value } },
        }
        return newState
      }),
    [debouncedSetDetention]
  )

  const onChangeText = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) =>
      setDetention((current: IDetention) => ({
        ...current,
        additionalInfo: {
          ...current.additionalInfo,
          externalNote: value,
        },
      })),
    [setDetention]
  )

  const onChangeDateTime = useCallback(
    ({ date, time }: ITimeDate, id: string) => {
      setDetention((current: IDetention) => {
        const newState = {
          ...current,
          [id]: `${date} ${time}`,
          [`${id}IsValid`]: !!date && !!time,
        }
        return newState
      })
    },
    [setDetention]
  )

  const onClick = useCallback((value: IVariant) => {
    setDialogVariant(value)
  }, [])

  const onConfirm = useCallback(
    (value: string) =>
      debouncedSetDetention((current: IDetention) => ({ ...current, state: value })),
    [debouncedSetDetention]
  )

  const hasValidDates = detention.startDateTimeIsValid && detention.endDateTimeIsValid
  const hasValidProps = !!calculatedAmount && !!Object.keys({ ...selectedStop }).length
  const isFormValid = hasValidDates && hasValidProps

  return {
    detention,
    setDetention,
    setDetentionRate,
    debouncedSetDetention,
    onChangeAmount,
    onChange,
    onChangeText,
    onChangeDateTime,
    onClick,
    onConfirm,
    dialogVariant,
    calculatedAmount,
    isFormValid,
    updateShipmentAccessorials,
  }
}

export default useDetention
